import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Phone, Clock, Package, FileText, CheckCircle,
  User, Pen, RefreshCw, Camera, X, ZoomIn, Play, CreditCard,
  MessageSquare, Plus, Trash2, ChevronDown, AlertCircle, Truck, Star,
  Download, Send
} from 'lucide-react';
import { DriverProvider, useDriver } from '../../contexts/DriverContext';
import { supabase } from '../../lib/supabase';
import driverPickupService from '../../services/driverPickupService';
import { jsPDF } from 'jspdf';

// ─── ADT Express Tariff Catalog ───────────────────────────────────────────────
const ADT_CATALOG = [
  { label: 'Barrique noire 270 L transport seul', unit_price: 140, dimensions: '270L', category: 'barrique' },
  { label: 'Barrique noire 270 L promo transport + barrique', unit_price: 160, dimensions: '270L', category: 'barrique' },
  { label: 'Barrique bleue 220 L transport', unit_price: 140, dimensions: '220L', category: 'barrique' },
  { label: 'Carton 60×50×40', unit_price: 70, dimensions: '60×50×40', category: 'carton' },
  { label: 'Carton petit 60×40×40', unit_price: 90, dimensions: '60×40×40', category: 'carton' },
  { label: 'Carton 80×50×50', unit_price: 100, dimensions: '80×50×50', category: 'carton' },
  { label: 'Carton 100×50×50', unit_price: 110, dimensions: '100×50×50', category: 'carton' },
  { label: 'Carton grand 100×80×80', unit_price: 180, dimensions: '100×80×80', category: 'carton' },
  { label: 'Valise 23 kg', unit_price: 70, dimensions: '23kg', category: 'valise' },
  { label: 'Sac cabas moyen', unit_price: 90, dimensions: 'moyen', category: 'sac' },
  { label: 'Sac cabas grand', unit_price: 130, dimensions: 'grand', category: 'sac' },
  { label: 'TV 55 pouces', unit_price: 300, dimensions: '55"', category: 'electromenager' },
  { label: 'TV 82 pouces', unit_price: 350, dimensions: '82"', category: 'electromenager' },
  { label: 'TV 127 pouces', unit_price: 450, dimensions: '127"', category: 'electromenager' },
  { label: 'Réfrigérateur petit', unit_price: 100, dimensions: 'petit', category: 'electromenager' },
  { label: 'Réfrigérateur moyen', unit_price: 150, dimensions: 'moyen', category: 'electromenager' },
  { label: 'Réfrigérateur grand', unit_price: 180, dimensions: 'grand', category: 'electromenager' },
  { label: 'Lave-linge', unit_price: 120, dimensions: 'standard', category: 'electromenager' },
  { label: 'Micro-ondes', unit_price: 60, dimensions: 'standard', category: 'electromenager' },
  { label: 'Cuisinière', unit_price: 130, dimensions: 'standard', category: 'electromenager' },
];

// ─── Navigation helpers ───────────────────────────────────────────────────────
const generateNavigationUrl = (type, address, postalCode, city) => {
  const query = `${address},${postalCode},${city},France`?.replace(/\s+/g, '+');
  if (type === 'google') return `https://www.google.com/maps/search/?api=1&query=${query}`;
  if (type === 'waze') return `https://waze.com/ul?q=${address},${postalCode},${city}&navigate=yes`;
  return '';
};

const NavigationButtons = ({ pickup }) => {
  if (!pickup?.clientAddress || !pickup?.clientCity) return null;
  const postalCode = pickup?.clientCity?.match(/\b\d{5}\b/)?.[0] || '';
  return (
    <div className="flex gap-2 mt-3">
      <a
        href={generateNavigationUrl('google', pickup?.clientAddress, postalCode, pickup?.clientCity)}
        target="_blank" rel="noopener noreferrer"
        className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition"
      >
        📍 Google Maps
      </a>
      <a
        href={generateNavigationUrl('waze', pickup?.clientAddress, postalCode, pickup?.clientCity)}
        target="_blank" rel="noopener noreferrer"
        className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition"
      >
        🚗 Waze
      </a>
    </div>
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const getStatusBadge = (status) => {
  const badges = {
    planifie: { text: 'Planifié', className: 'bg-blue-100 text-blue-800' },
    en_route: { text: 'En route', className: 'bg-indigo-100 text-indigo-800' },
    en_cours: { text: 'En cours', className: 'bg-yellow-100 text-yellow-800' },
    collecte: { text: 'Collecté', className: 'bg-orange-100 text-orange-800' },
    depose: { text: 'Déposé entrepôt', className: 'bg-purple-100 text-purple-800' },
    termine: { text: 'Terminé', className: 'bg-green-100 text-green-800' },
    annule: { text: 'Annulé', className: 'bg-red-100 text-red-800' },
  };
  const badge = badges?.[status] || badges?.planifie;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge?.className}`}>
      {badge?.text}
    </span>
  );
};

// ─── Collected Items Section ──────────────────────────────────────────────────
function CollectedItemsSection({ items, onChange }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState('');

  const totalExpected = items?.reduce((sum, item) => sum + (item?.total || 0), 0);

  const handleAddItem = () => {
    if (!selectedCatalog) return;
    const catalogItem = ADT_CATALOG?.find(c => c?.label === selectedCatalog);
    if (!catalogItem) return;
    const newItem = {
      id: Date.now(),
      label: catalogItem?.label,
      quantity: 1,
      unit_price: catalogItem?.unit_price,
      total: catalogItem?.unit_price,
      dimensions: catalogItem?.dimensions,
      category: catalogItem?.category,
    };
    onChange([...(items || []), newItem]);
    setSelectedCatalog('');
    setShowDropdown(false);
  };

  const handleQuantityChange = (id, qty) => {
    const q = Math.max(1, parseInt(qty) || 1);
    onChange(items?.map(item =>
      item?.id === id ? { ...item, quantity: q, total: q * item?.unit_price } : item
    ));
  };

  const handlePriceChange = (id, newPrice) => {
    const p = parseFloat(newPrice) || 0;
    onChange(items?.map(item =>
      item?.id === id ? { ...item, unit_price: p, total: item?.quantity * p } : item
    ));
  };

  const handleRemoveItem = (id) => {
    onChange(items?.filter(item => item?.id !== id));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
          <Package className="w-4 h-4 text-blue-600" />
        </div>
        <h3 className="font-semibold text-gray-800">Colis enlevés</h3>
      </div>

      {/* Existing items */}
      {items?.length > 0 && (
        <div className="space-y-2 mb-3">
          {items?.map(item => (
            <div key={item?.id} className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item?.label}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item?.unit_price}
                    onChange={e => handlePriceChange(item?.id, e?.target?.value)}
                    className="w-20 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-500">€ / unité</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleQuantityChange(item?.id, item?.quantity - 1)}
                  className="w-6 h-6 rounded bg-white border border-gray-300 text-gray-600 flex items-center justify-center text-sm hover:bg-gray-50"
                >−</button>
                <input
                  type="number"
                  min="1"
                  value={item?.quantity}
                  onChange={e => handleQuantityChange(item?.id, e?.target?.value)}
                  className="w-10 text-center border border-gray-300 rounded text-sm py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => handleQuantityChange(item?.id, item?.quantity + 1)}
                  className="w-6 h-6 rounded bg-white border border-gray-300 text-gray-600 flex items-center justify-center text-sm hover:bg-gray-50"
                >+</button>
              </div>
              <span className="text-sm font-bold text-blue-700 w-16 text-right">{item?.total} €</span>
              <button
                type="button"
                onClick={() => handleRemoveItem(item?.id)}
                className="text-red-400 hover:text-red-600 transition ml-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add item */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <select
            value={selectedCatalog}
            onChange={e => setSelectedCatalog(e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none pr-8"
          >
            <option value="">Choisir un type de colis...</option>
            {ADT_CATALOG?.map(item => (
              <option key={item?.label} value={item?.label}>
                {item?.label} — {item?.unit_price} €
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <button
          type="button"
          onClick={handleAddItem}
          disabled={!selectedCatalog}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition"
        >
          <Plus className="w-4 h-4" />
          Ajouter
        </button>
      </div>

      {/* Total */}
      {items?.length > 0 && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex justify-between text-sm font-bold text-gray-900">
            <span>Total attendu :</span>
            <span className="text-blue-700">{totalExpected} €</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Payment Section ──────────────────────────────────────────────────────────
function PaymentSection({ items, payment, onChange, discount, onDiscountChange }) {
  const totalExpected = items?.reduce((sum, item) => sum + (item?.total || 0), 0);
  const discountAmount = parseFloat(discount) || 0;
  const totalAfterDiscount = totalExpected - discountAmount;
  const collected = parseFloat(payment?.amountCollected) || 0;
  const remaining = totalAfterDiscount - collected;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-green-600" />
        </div>
        <h3 className="font-semibold text-gray-800">Paiement</h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total attendu :</span>
            <span className="font-semibold text-gray-900">{totalExpected} €</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Remise :</span>
            <span className="font-semibold text-orange-600">− {discountAmount} €</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Montant encaissé :</span>
            <span className="font-semibold text-green-700">{collected} €</span>
          </div>
          <div className={`flex justify-between text-sm font-bold border-t pt-1 mt-1 ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
            <span>Reste à payer :</span>
            <span>{remaining} €</span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Montant encaissé (€)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={payment?.amountCollected || ''}
            onChange={e => onChange({ ...payment, amountCollected: e?.target?.value })}
            placeholder="0.00"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Remise (€)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={discount || ''}
            onChange={e => onDiscountChange(e?.target?.value)}
            placeholder="0.00"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Mode de paiement</label>
          <select
            value={payment?.method || 'especes'}
            onChange={e => onChange({ ...payment, method: e?.target?.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="especes">Espèces</option>
            <option value="carte">Carte bancaire</option>
            <option value="virement">Virement</option>
            <option value="cheque">Chèque</option>
            <option value="aucun">Aucun paiement</option>
          </select>
        </div>

        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Notes paiement</label>
          <input
            type="text"
            value={payment?.notes || ''}
            onChange={e => onChange({ ...payment, notes: e?.target?.value })}
            placeholder="Ex: Reçu remis au client"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Receipt Modal ────────────────────────────────────────────────────────────
function ReceiptModal({ onDownload, onWhatsApp, onClose, clientPhone }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-3">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Reçu de ramassage</h3>
          <p className="text-sm text-gray-500 mt-1">Que souhaitez-vous faire avec le reçu ?</p>
        </div>
        <div className="space-y-3">
          <button
            onClick={onDownload}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition"
          >
            <Download className="w-5 h-5" />
            Télécharger le reçu (PDF)
          </button>
          {clientPhone && (
            <button
              onClick={onWhatsApp}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition"
            >
              <Send className="w-5 h-5" />
              Envoyer par WhatsApp
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition text-sm"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Generate Receipt PDF ─────────────────────────────────────────────────────
const generateReceiptPDF = ({ pickup, collectedItems, payment, discount, employee, driverRating }) => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const margin = 15;
  let y = margin;

  const totalExpected = collectedItems?.reduce((sum, item) => sum + (item?.total || 0), 0) || 0;
  const discountAmount = parseFloat(discount) || 0;
  const totalAfterDiscount = totalExpected - discountAmount;
  const amountCollected = parseFloat(payment?.amountCollected) || 0;
  const now = new Date();

  // ── Header ──
  doc?.setFillColor(30, 58, 95);
  doc?.rect(0, 0, pageWidth, 30, 'F');
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(16);
  doc?.setFont(undefined, 'bold');
  doc?.text('ADT Express — Reçu de ramassage', pageWidth / 2, 18, { align: 'center' });
  doc?.setTextColor(0, 0, 0);
  y = 40;

  // ── Date / Client ──
  doc?.setFontSize(10);
  doc?.setFont(undefined, 'normal');
  doc?.text(`Date : ${now?.toLocaleDateString('fr-FR')}  ${now?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`, margin, y);
  y += 7;
  doc?.text(`Client : ${pickup?.clientName || '—'}`, margin, y);
  y += 7;
  if (pickup?.clientPhone) {
    doc?.text(`Téléphone : ${pickup?.clientPhone}`, margin, y);
    y += 7;
  }
  if (pickup?.clientAddress) {
    doc?.text(`Adresse : ${pickup?.clientAddress}, ${pickup?.clientCity || ''}`, margin, y);
    y += 7;
  }
  y += 4;

  // ── Separator ──
  doc?.setLineWidth(0.4);
  doc?.line(margin, y, pageWidth - margin, y);
  y += 8;

  // ── Items table header ──
  doc?.setFont(undefined, 'bold');
  doc?.setFontSize(9);
  doc?.text('Désignation', margin, y);
  doc?.text('Qté', margin + 90, y, { align: 'right' });
  doc?.text('P.U. (€)', margin + 120, y, { align: 'right' });
  doc?.text('Total (€)', margin + 155, y, { align: 'right' });
  y += 5;
  doc?.setLineWidth(0.2);
  doc?.line(margin, y, pageWidth - margin, y);
  y += 6;

  // ── Items ──
  doc?.setFont(undefined, 'normal');
  collectedItems?.forEach(item => {
    const labelLines = doc?.splitTextToSize(item?.label || '', 85);
    labelLines?.forEach((line, i) => {
      doc?.text(line, margin, y + i * 5);
    });
    const rowY = y + (labelLines?.length - 1) * 5;
    doc?.text(String(item?.quantity || 1), margin + 90, rowY, { align: 'right' });
    doc?.text(String(item?.unit_price || 0), margin + 120, rowY, { align: 'right' });
    doc?.text(String(item?.total || 0), margin + 155, rowY, { align: 'right' });
    y += labelLines?.length * 5 + 3;
  });

  y += 3;
  doc?.setLineWidth(0.4);
  doc?.line(margin, y, pageWidth - margin, y);
  y += 8;

  // ── Totals ──
  doc?.setFontSize(10);
  doc?.setFont(undefined, 'normal');
  doc?.text('Total attendu :', margin + 80, y);
  doc?.setFont(undefined, 'bold');
  doc?.text(`${totalExpected} €`, pageWidth - margin, y, { align: 'right' });
  y += 7;

  if (discountAmount > 0) {
    doc?.setFont(undefined, 'normal');
    doc?.text('Remise appliquée :', margin + 80, y);
    doc?.setFont(undefined, 'bold');
    doc?.setTextColor(200, 80, 0);
    doc?.text(`− ${discountAmount} €`, pageWidth - margin, y, { align: 'right' });
    doc?.setTextColor(0, 0, 0);
    y += 7;
    doc?.setFont(undefined, 'normal');
    doc?.text('Total après remise :', margin + 80, y);
    doc?.setFont(undefined, 'bold');
    doc?.text(`${totalAfterDiscount} €`, pageWidth - margin, y, { align: 'right' });
    y += 7;
  }

  doc?.setFont(undefined, 'normal');
  doc?.text('Montant encaissé :', margin + 80, y);
  doc?.setFont(undefined, 'bold');
  doc?.setTextColor(0, 130, 60);
  doc?.text(`${amountCollected} €`, pageWidth - margin, y, { align: 'right' });
  doc?.setTextColor(0, 0, 0);
  y += 10;

  // ── Driver ──
  doc?.setLineWidth(0.2);
  doc?.line(margin, y, pageWidth - margin, y);
  y += 8;
  doc?.setFont(undefined, 'normal');
  doc?.setFontSize(10);
  doc?.text(`Objets enlevés par : ${employee?.name || '—'}`, margin, y);
  y += 7;

  // ── Rating ──
  if (driverRating) {
    const stars = '⭐'?.repeat(driverRating);
    doc?.text(`Note attribuée : ${driverRating}/5 ${stars}`, margin, y);
    y += 7;
  }

  // ── Footer ──
  y += 5;
  doc?.setFontSize(8);
  doc?.setFont(undefined, 'italic');
  doc?.setTextColor(120, 120, 120);
  doc?.text('ADT Express — Votre partenaire de confiance pour l\'envoi de colis vers l\'Afrique', pageWidth / 2, y, { align: 'center' });
  y += 5;
  doc?.text(`Généré le ${now?.toLocaleString('fr-FR')}`, pageWidth / 2, y, { align: 'center' });

  return doc;
};

// ─── Signature Canvas Component ───────────────────────────────────────────────
function SignatureSection({ onSignatureValidated, loading, driverRating, onDriverRatingChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [validated, setValidated] = useState(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas?.getBoundingClientRect();
    const scaleX = canvas?.width / rect?.width;
    const scaleY = canvas?.height / rect?.height;
    if (e?.touches) {
      return {
        x: (e?.touches?.[0]?.clientX - rect?.left) * scaleX,
        y: (e?.touches?.[0]?.clientY - rect?.top) * scaleY,
      };
    }
    return {
      x: (e?.clientX - rect?.left) * scaleX,
      y: (e?.clientY - rect?.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e?.preventDefault();
    const canvas = canvasRef?.current;
    if (!canvas) return;
    setIsDrawing(true);
    const pos = getPos(e, canvas);
    lastPos.current = pos;
    const ctx = canvas?.getContext('2d');
    ctx?.beginPath();
    ctx?.arc(pos?.x, pos?.y, 1.5, 0, Math.PI * 2);
    ctx.fillStyle = '#1e3a5f';
    ctx?.fill();
    setHasSignature(true);
  };

  const draw = (e) => {
    e?.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    const pos = getPos(e, canvas);
    ctx?.beginPath();
    ctx?.moveTo(lastPos?.current?.x, lastPos?.current?.y);
    ctx?.lineTo(pos?.x, pos?.y);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx?.stroke();
    lastPos.current = pos;
  };

  const stopDraw = (e) => {
    e?.preventDefault();
    setIsDrawing(false);
    lastPos.current = null;
  };

  const handleClear = () => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
    setSignaturePreview(null);
    setValidated(false);
  };

  const handleValidate = async () => {
    const canvas = canvasRef?.current;
    if (!canvas || !hasSignature) {
      alert('Veuillez capturer la signature du client avant de valider.');
      return;
    }
    const preview = canvas?.toDataURL('image/png');
    setSignaturePreview(preview);
    setValidated(true);

    // Convert to blob and pass up
    canvas?.toBlob(async (blob) => {
      if (blob) {
        await onSignatureValidated(blob, preview);
      }
    }, 'image/png');
  };

  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
          <Pen className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Signature client obligatoire</h3>
          <p className="text-xs text-amber-700">La collecte ne peut pas être validée sans signature</p>
        </div>
      </div>

      {validated && signaturePreview ? (
        <div>
          <div className="border-2 border-green-400 rounded-lg overflow-hidden bg-white mb-3">
            <img src={signaturePreview} alt="Signature validée" className="w-full h-32 object-contain" />
          </div>
          <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Signature enregistrée avec succès</span>
          </div>

          {/* ── Star Rating ── */}
          <div className="mt-4 p-4 bg-white border border-amber-200 rounded-xl">
            <p className="text-sm font-medium text-gray-700 mb-2">Le chauffeur a été <span className="text-gray-400 font-normal">(optionnel)</span></p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5]?.map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => onDriverRatingChange(driverRating === star ? null : star)}
                  className="focus:outline-none transition-transform hover:scale-110"
                  aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
                >
                  <Star
                    className={`w-8 h-8 ${driverRating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                  />
                </button>
              ))}
            </div>
            {driverRating && (
              <p className="text-xs text-gray-500 mt-1">Note : {driverRating}/5 étoile{driverRating > 1 ? 's' : ''}</p>
            )}
          </div>

          <button
            type="button"
            onClick={() => { handleClear(); setValidated(false); }}
            className="mt-2 w-full text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Recommencer la signature
          </button>
        </div>
      ) : (
        <>
          <div className="border-2 border-gray-300 rounded-lg bg-white overflow-hidden mb-3 relative">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full h-40 cursor-crosshair touch-none"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
            {!hasSignature && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-gray-300 text-sm">Signez ici avec le doigt ou la souris</p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 flex items-center justify-center gap-2 border border-gray-300 text-gray-700 font-medium py-2.5 rounded-lg hover:bg-gray-50 transition text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Effacer
            </button>
            <button
              type="button"
              onClick={handleValidate}
              disabled={!hasSignature || loading}
              className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white font-semibold py-2.5 rounded-lg transition text-sm"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Valider la signature
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Photos Section ───────────────────────────────────────────────────────────
function PhotosSection({ photoBlobs, photoPreviews, onAdd, onRemove }) {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (photoBlobs?.length >= 5) { alert('Maximum 5 photos autorisées'); return; }
    const reader = new FileReader();
    reader.onloadend = () => onAdd(file, reader?.result);
    reader?.readAsDataURL(file);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center">
          <Camera className="w-4 h-4 text-purple-600" />
        </div>
        <h3 className="font-semibold text-gray-800">Photos des colis ({photoBlobs?.length}/5)</h3>
      </div>

      {photoPreviews?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photoPreviews?.map((preview, index) => (
            <div key={index} className="relative rounded-lg overflow-hidden border border-gray-200">
              <img src={preview} alt={`Photo ${index + 1}`} className="w-full h-24 object-cover" />
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {photoBlobs?.length < 5 && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef?.current?.click()}
            className="w-full border-2 border-dashed border-purple-300 rounded-lg py-3 text-sm text-purple-600 hover:border-purple-400 hover:bg-purple-50 transition flex items-center justify-center gap-2"
          >
            <Camera className="w-4 h-4" />
            Ajouter une photo
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function DriverPickupDetailContent() {
  const navigate = useNavigate();
  const { pickupId } = useParams();
  const { employee } = useDriver();

  const [pickup, setPickup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Collected items state
  const [collectedItems, setCollectedItems] = useState([]);

  // Payment state
  const [payment, setPayment] = useState({ amountCollected: '', method: 'especes', notes: '' });

  // Discount state
  const [discount, setDiscount] = useState(0);

  // Photos state
  const [photoBlobs, setPhotoBlobs] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  // Signature state
  const [signatureBlob, setSignatureBlob] = useState(null);
  const [signatureValidated, setSignatureValidated] = useState(false);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null);

  // Completed state
  const [photoSignedUrls, setPhotoSignedUrls] = useState([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);

  // Remarks
  const [remarks, setRemarks] = useState('');

  // Active step for the workflow
  const [activeStep, setActiveStep] = useState(null);

  // Driver rating state
  const [driverRating, setDriverRating] = useState(null);

  // Receipt modal state
  const [receiptDoc, setReceiptDoc] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => { loadPickup(); }, [pickupId]);

  const loadPickup = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await driverPickupService?.getPickupById(pickupId);
      setPickup(data);

      // ✅ Pre-fill collected items from existing data OR parsed from notes
      if (data?.collectedItems && Array.isArray(data?.collectedItems) && data?.collectedItems?.length > 0) {
        setCollectedItems(data?.collectedItems?.map((item, idx) => ({ ...item, id: item?.id || Date.now() + idx })));
      }
      // Note: if collectedItems is empty, getPickupById already parsed notes into collectedItems

      // Pre-fill payment
      if (data?.amountCollected || data?.paymentAmount) {
        setPayment({
          amountCollected: (data?.amountCollected || data?.paymentAmount || '')?.toString(),
          method: data?.paymentMethod || 'especes',
          notes: data?.paymentNotes || '',
        });
      }

      if (data?.pickupSignatureUrl) {
        setSignaturePreviewUrl(data?.pickupSignatureUrl);
        setSignatureValidated(true);
      }

      if (data?.status === 'termine' || data?.status === 'depose') {
        await generatePhotoSignedUrls(data);
      }
    } catch (err) {
      setError('Erreur lors du chargement de la collecte');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generatePhotoSignedUrls = async (pickupData) => {
    try {
      const photos = pickupData?.pickupPhotoUrls || [];
      if (photos?.length === 0) return;
      const urls = await Promise.all(
        photos?.map(async (filePath) => {
          const path = filePath?.startsWith('pickup-photos/') ? filePath : `pickup-photos/${filePath}`;
          const { data } = await supabase?.storage?.from('shipment-photos')?.createSignedUrl(path, 3600);
          return data?.signedUrl || null;
        })
      );
      setPhotoSignedUrls(urls?.filter(Boolean));
    } catch (err) {
      console.error('Error generating signed URLs:', err);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setActionLoading(true);
    try {
      await driverPickupService?.updatePickupStatus(pickupId, newStatus);
      const refreshed = await driverPickupService?.getPickupById(pickupId);
      setPickup(refreshed);
    } catch (err) {
      alert('Erreur: ' + (err?.message || 'Erreur inconnue'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSignatureValidated = async (blob, previewDataUrl) => {
    setSignatureBlob(blob);
    setSignaturePreviewUrl(previewDataUrl);
    setSignatureValidated(true);
  };

  const handleAddPhoto = (file, preview) => {
    setPhotoBlobs(prev => [...prev, file]);
    setPhotoPreviews(prev => [...prev, preview]);
  };

  const handleRemovePhoto = (index) => {
    setPhotoBlobs(prev => prev?.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev?.filter((_, i) => i !== index));
  };

  const handleConfirmCollection = async () => {
    if (!signatureValidated || !signatureBlob) {
      alert('⚠️ La signature client est obligatoire avant de confirmer la collecte.');
      return;
    }

    setActionLoading(true);
    try {
      // 1. Upload signature
      const signatureUrl = await driverPickupService?.uploadSignatureAndComplete(pickupId, signatureBlob);

      // 2. Upload photos
      const uploadedPhotoPaths = [];
      for (const photoBlob of photoBlobs) {
        try {
          const path = await driverPickupService?.uploadPickupPhoto(pickupId, photoBlob);
          uploadedPhotoPaths?.push(path);
        } catch (photoErr) {
          console.warn('Photo upload failed (non-blocking):', photoErr);
        }
      }

      // 3. Save collected items, payment, remarks to pickup_tasks
      const cleanItems = collectedItems?.map(({ id, ...rest }) => rest);
      const totalAmount = collectedItems?.reduce((sum, item) => sum + (item?.total || 0), 0) || 0;
      const amountPaid = parseFloat(payment?.amountCollected) || 0;
      const discountAmount = parseFloat(discount) || 0;

      const updatePayload = {
        collected_items: cleanItems,
        payment_amount: totalAmount,
        payment_method: payment?.method,
        payment_notes: payment?.notes,
        amount_collected: amountPaid,
        discount_amount: discountAmount,
        notes: remarks || pickup?.notes,
        status: 'collecte',
        pickup_signature_url: signatureUrl,
        driver_rating: driverRating,
      };

      const { error: updateError } = await supabase
        ?.from('pickup_tasks')
        ?.update(updatePayload)
        ?.eq('id', pickupId);

      if (updateError) throw updateError;

      // 4. Find or create client in clients table
      const clientId = await driverPickupService?.findOrCreateClient({
        name: pickup?.clientName,
        phone: pickup?.clientPhone,
        email: pickup?.clientEmail || null,
        address: pickup?.clientAddress,
        city: pickup?.clientCity,
      });

      // 5. Create or update shipment — the core business logic
      const allPhotoUrls = [
        ...(pickup?.pickupPhotoUrls || []),
        ...uploadedPhotoPaths,
      ];

      await driverPickupService?.createOrUpdateShipmentFromPickup({
        pickupId,
        pickupData: pickup,
        collectedItems,
        signatureUrl,
        photoUrls: allPhotoUrls,
        payment,
        discount: discountAmount,
        driverId: employee?.id || null,
        clientId,
      });

      // 6. Reload
      const refreshed = await driverPickupService?.getPickupById(pickupId);
      setPickup(refreshed);
      setPhotoBlobs([]);
      setPhotoPreviews([]);
      setActiveStep(null);
      await generatePhotoSignedUrls(refreshed);

      // 7. Generate receipt PDF
      try {
        const doc = generateReceiptPDF({
          pickup,
          collectedItems,
          payment,
          discount: discountAmount,
          employee,
          driverRating,
        });
        setReceiptDoc(doc);
        setShowReceiptModal(true);
      } catch (pdfErr) {
        console.warn('Receipt PDF generation failed (non-blocking):', pdfErr);
        alert('✅ Collecte confirmée ! Signature, photos et colis enregistrés. Un envoi ADT Express a été créé automatiquement.');
      }
    } catch (err) {
      alert('Erreur lors de la confirmation: ' + (err?.message || 'Erreur inconnue'));
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !pickup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <p className="text-red-600 mb-4">{error || 'Collecte introuvable'}</p>
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const isCompleted = pickup?.status === 'termine' || pickup?.status === 'depose';
  const canConfirm = signatureValidated;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Retour</span>
          </button>
          {getStatusBadge(pickup?.status)}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">

        {/* ── Client Info Card ── */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Informations de la collecte</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Créneau horaire</p>
                <p className="font-semibold text-gray-900">{pickup?.timeWindow || '—'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 pt-3 border-t">
              <User className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Client</p>
                <p className="font-semibold text-gray-900">{pickup?.clientName}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Téléphone</p>
                <a href={`tel:${pickup?.clientPhone}`} className="font-semibold text-blue-600 hover:text-blue-700">
                  {pickup?.clientPhone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 pt-3 border-t">
              <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Adresse</p>
                <p className="font-semibold text-gray-900">{pickup?.clientAddress}</p>
                <p className="text-sm text-gray-600">{pickup?.clientCity} {pickup?.clientZone && `— Zone ${pickup?.clientZone}`}</p>
                <NavigationButtons pickup={pickup} />
              </div>
            </div>
            {pickup?.shipmentReference && (
              <div className="flex items-start gap-3 pt-3 border-t">
                <Package className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Référence envoi</p>
                  <p className="font-semibold text-gray-900">{pickup?.shipmentReference}</p>
                  {pickup?.shipmentDescription && <p className="text-sm text-gray-600 mt-0.5">{pickup?.shipmentDescription}</p>}
                </div>
              </div>
            )}
            {pickup?.notes && (
              <div className="flex items-start gap-3 pt-3 border-t">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Notes</p>
                  <p className="text-sm text-gray-900 whitespace-pre-line">{pickup?.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Workflow Steps (active statuses) ── */}
        {!isCompleted && (
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Progression de la collecte</h3>
            <div className="space-y-2">

              {/* Step 1: En route */}
              {(pickup?.status === 'planifie') && (
                <button
                  onClick={() => handleStatusUpdate('en_route')}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-lg transition text-sm"
                >
                  <Truck className="w-4 h-4" />
                  En route vers le client
                </button>
              )}

              {/* Step 2: Démarrer la collecte */}
              {(pickup?.status === 'planifie' || pickup?.status === 'en_route') && (
                <button
                  onClick={() => { handleStatusUpdate('en_cours'); setActiveStep('collect'); }}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold rounded-lg transition text-sm"
                >
                  <Play className="w-4 h-4" />
                  Démarrer la collecte
                </button>
              )}

              {/* Step 3: Faire signer */}
              {pickup?.status === 'en_cours' && (
                <button
                  onClick={() => setActiveStep('sign')}
                  disabled={actionLoading}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 font-semibold rounded-lg transition text-sm ${signatureValidated ? 'bg-green-100 text-green-800 border-2 border-green-400' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                >
                  <Pen className="w-4 h-4" />
                  {signatureValidated ? '✅ Signature obtenue' : 'Faire signer le client'}
                </button>
              )}

              {/* Step 4: Confirmer la collecte */}
              {pickup?.status === 'en_cours' && (
                <button
                  onClick={handleConfirmCollection}
                  disabled={actionLoading || !canConfirm}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 font-semibold rounded-lg transition text-sm ${!canConfirm ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-700 hover:bg-blue-800 text-white'}`}
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Confirmer la collecte
                </button>
              )}

              {/* Step 5: Déposé à l'entrepôt */}
              {pickup?.status === 'collecte' && (
                <button
                  onClick={() => handleStatusUpdate('depose')}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-semibold rounded-lg transition text-sm"
                >
                  📦 Déposé à l'entrepôt
                </button>
              )}

              {/* Warning if signature missing */}
              {pickup?.status === 'en_cours' && !signatureValidated && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">La signature client est obligatoire pour confirmer la collecte.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Collection Form (shown when en_cours) ── */}
        {pickup?.status === 'en_cours' && (
          <div className="space-y-4">

            {/* Colis enlevés */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <CollectedItemsSection items={collectedItems} onChange={setCollectedItems} />
            </div>

            {/* Paiement */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <PaymentSection items={collectedItems} payment={payment} onChange={setPayment} discount={discount} onDiscountChange={setDiscount} />
            </div>

            {/* Photos */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <PhotosSection
                photoBlobs={photoBlobs}
                photoPreviews={photoPreviews}
                onAdd={handleAddPhoto}
                onRemove={handleRemovePhoto}
              />
            </div>

            {/* Signature — always visible when en_cours */}
            <SignatureSection
              onSignatureValidated={handleSignatureValidated}
              loading={actionLoading}
              driverRating={driverRating}
              onDriverRatingChange={setDriverRating}
            />

            {/* Remarks */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-gray-600" />
                </div>
                <h3 className="font-semibold text-gray-800">Remarques</h3>
              </div>
              <textarea
                value={remarks}
                onChange={e => setRemarks(e?.target?.value)}
                placeholder="Observations, état des colis, problèmes rencontrés..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
              />
            </div>

            {/* Confirm button (bottom) */}
            <button
              onClick={handleConfirmCollection}
              disabled={actionLoading || !canConfirm}
              className={`w-full flex items-center justify-center gap-3 py-4 font-bold rounded-xl transition text-base shadow-lg ${!canConfirm ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-700 hover:bg-blue-800 text-white shadow-blue-200'}`}
            >
              {actionLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              {canConfirm ? 'Confirmer la collecte' : 'Signature requise pour confirmer'}
            </button>
          </div>
        )}

        {/* ── Completed View ── */}
        {isCompleted && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Collecte terminée</h3>
              <p className="text-gray-500 text-sm">Signature, photos et colis enregistrés</p>
            </div>

            {/* Collected items summary */}
            {collectedItems?.length > 0 && (
              <div className="mb-5">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Colis collectés :</h4>
                <div className="space-y-1">
                  {collectedItems?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm bg-blue-50 rounded-lg px-3 py-2">
                      <span className="text-gray-800">{item?.quantity}× {item?.label}</span>
                      <span className="font-semibold text-blue-700">{item?.total} €</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-bold border-t pt-2 mt-1">
                    <span>Total :</span>
                    <span className="text-blue-700">{collectedItems?.reduce((s, i) => s + (i?.total || 0), 0)} €</span>
                  </div>
                </div>
              </div>
            )}

            {/* Photos */}
            {photoSignedUrls?.length > 0 && (
              <div className="mb-5">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Photos ({photoSignedUrls?.length}) :</h4>
                <div className="grid grid-cols-3 gap-2">
                  {photoSignedUrls?.map((url, index) => (
                    <div
                      key={index}
                      className="relative rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:border-blue-400 transition group"
                      onClick={() => setSelectedPhotoIndex(index)}
                    >
                      <img src={url} alt={`Photo ${index + 1}`} className="w-full h-24 object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition flex items-center justify-center">
                        <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Signature */}
            {(signaturePreviewUrl || pickup?.pickupSignatureUrl || pickup?.clientSignatureUrl) && (
              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <p className="text-sm font-semibold text-gray-700 mb-2">Signature du client :</p>
                <img
                  src={signaturePreviewUrl || pickup?.pickupSignatureUrl || pickup?.clientSignatureUrl}
                  alt="Signature du client"
                  className="w-full h-auto max-h-48 object-contain bg-white border border-gray-200 rounded"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && receiptDoc && (
        <ReceiptModal
          clientPhone={pickup?.clientPhone}
          onDownload={() => {
            const clientName = pickup?.clientName?.replace(/\s+/g, '_') || 'client';
            const dateStr = new Date()?.toISOString()?.split('T')?.[0];
            receiptDoc?.save(`recu_ramassage_${clientName}_${dateStr}.pdf`);
            setShowReceiptModal(false);
          }}
          onWhatsApp={() => {
            const rawPhone = pickup?.clientPhone?.replace(/[\s\-().+]/g, '') || '';
            const phone = rawPhone?.startsWith('0') ? '33' + rawPhone?.slice(1) : rawPhone;
            const text = encodeURIComponent(
              `Bonjour, voici votre reçu de ramassage ADT Express du ${new Date()?.toLocaleDateString('fr-FR')}. ` +
              `Le reçu PDF vous sera transmis séparément par votre chauffeur. Merci de votre confiance !`
            );
            window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
            setShowReceiptModal(false);
          }}
          onClose={() => {
            setShowReceiptModal(false);
            alert('✅ Collecte confirmée ! Signature, photos et colis enregistrés. Un envoi ADT Express a été créé automatiquement.');
          }}
        />
      )}

      {/* Lightbox */}
      {selectedPhotoIndex !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhotoIndex(null)}
        >
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setSelectedPhotoIndex(null)}
              className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-200 transition z-10"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={photoSignedUrls?.[selectedPhotoIndex]}
              alt={`Photo ${selectedPhotoIndex + 1}`}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              onClick={e => e?.stopPropagation()}
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full text-sm font-medium">
              {selectedPhotoIndex + 1} / {photoSignedUrls?.length}
            </div>
            {selectedPhotoIndex > 0 && (
              <button
                onClick={e => { e?.stopPropagation(); setSelectedPhotoIndex(selectedPhotoIndex - 1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 hover:bg-gray-200 transition"
              >←</button>
            )}
            {selectedPhotoIndex < photoSignedUrls?.length - 1 && (
              <button
                onClick={e => { e?.stopPropagation(); setSelectedPhotoIndex(selectedPhotoIndex + 1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 hover:bg-gray-200 transition"
              >→</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DriverPickupDetailWithIntegratedSignature() {
  return (
    <DriverProvider>
      <DriverPickupDetailContent />
    </DriverProvider>
  );
}